import {
	CountryEnum,
	type PartnerAddressResponseDto,
	PartnerAddressType,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { getFormValuesDiff } from "Infrastructure/Utilities/Forms/Helpers/getFormValuesDiff";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useUpdateAddress } from "Partner/Api/Mutation/updatePartnerAddress";
import {
	getPartnerAddressInfoEditSchema,
	type PartnerAddressInfoEditFormModel,
} from "Partner/Components/Form/Schema/partnerAddressInfoEditSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 } from "@mui/material";
import { useForm } from "react-hook-form";

const addressInfoResources = Resources.base.partner.detail.addressInfo;

interface AddressInfoFormProps {
	partnerAddressData?: PartnerAddressResponseDto | null;
	partnerId: number;
	isFormDisabled?: boolean;
}

export function AddressInfoForm({
	partnerAddressData,
	partnerId,
	isFormDisabled,
}: AddressInfoFormProps) {
	const { t } = useResource();
	const form = useForm<PartnerAddressInfoEditFormModel>({
		resolver: zodResolver(getPartnerAddressInfoEditSchema()),
		defaultValues: partnerAddressData || undefined,
	});

	const {
		control,
		formState: { errors, isDirty, dirtyFields },
	} = form;

	const { mutate: updateAddress, isPending } = useUpdateAddress(partnerId);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError((data) => {
			const changedValues = getFormValuesDiff(dirtyFields, {
				...data,
				type: PartnerAddressType.PRIMARY,
			});

			if (Object.keys(changedValues).length === 0) {
				return;
			}

			return (
				partnerAddressData?.id &&
				updateAddress({ data: changedValues, id: partnerAddressData?.id })
			);
		});
	};

	return (
		<>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="street"
						name="street"
						label={t(addressInfoResources.street)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="city"
						name="city"
						label={t(addressInfoResources.city)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="building-number"
						name="buildingNumber"
						label={t(addressInfoResources.builduingNumber)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="post-code"
						name="postCode"
						label={t(addressInfoResources.postCode)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, lg: 6 }}>
					<Select
						id="country"
						label={t(addressInfoResources.country)}
						fullWidth
						options={createSelectOptions(CountryEnum)}
						variant="outlined"
						name="country"
						control={control}
						errors={errors}
					/>
				</Grid2>
			</Grid2>

			<Button
				variant="contained"
				color="primary"
				onClick={handleSubmitUpdate}
				disabled={isFormDisabled || !isDirty}
				loading={isPending}
				sx={{ mt: 2 }}
			>
				{t(addressInfoResources.update)}
			</Button>
		</>
	);
}
